import React, { useState, useEffect, useMemo } from 'react';
import './Prediction.css';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { Chip } from '@mui/material';
import { Card, Button, Spin, Empty, message, Modal } from 'antd';
import axios from 'axios';
import { setLoginSuccess } from '../components/redux/reducer/authReducer';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';


const baseURL = process.env.REACT_APP_BASE_URL;

// API Functions
const fetchTodaySchedule = async () => {
  const response = await fetch(`${baseURL}/schedule/today`);
  if (!response.ok) {
    throw new Error('Failed to fetch schedule');
  }
  return response.json();
};

const fetchUserPredictions = async (userId) => {
  const response = await fetch(`${baseURL}/predictions/my?&userId=${userId}`);
  if (!response.ok) {
    throw new Error('Failed to fetch predictions');
  }
  return response.json();
};

const fetchTodayPrediction = async (userId) => {
  const response = await fetch(`${baseURL}/predictions/my?userId=${userId}&today=true`);
  if (!response.ok) {
    throw new Error('Failed to fetch today\'s prediction');
  }
  return response.json();
};

const fetchLeaderboard = async () => {
  const response = await fetch(`${baseURL}/predictions/leaderboard`);
  if (!response.ok) {
    throw new Error('Failed to fetch leaderboard');
  }
  return response.json();
};

const savePrediction = async (userId, matchId, predictedWinner) => {
  const payload = { userId, matchId, predictedWinner };
  console.log('💾 Saving prediction with payload:', payload);
  
  if (!userId) {
    throw new Error('❌ userId is missing! Cannot save prediction.');
  }
  if (!matchId) {
    throw new Error('❌ matchId is missing! Cannot save prediction.');
  }
  
  const response = await axios.post(`${baseURL}/predictions/save`, payload);
  // console.log('✅ Prediction saved successfully:', response.data);
  return response.data;
};

const fetchUserHistory = async (userId) => {
  const response = await fetch(`${baseURL}/predictions/my?userId=${userId}`);
  if (!response.ok) {
    throw new Error('Failed to fetch user history');
  }
  return response.json();
};

export default function Prediction() {
  const dispatch = useDispatch();
  const [selectedPredictions, setSelectedPredictions] = useState({}); // matchId -> team
  const [savingMatchId, setSavingMatchId] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const userProfile = useSelector((state) => state.login.userProfile);
  const isLoggedIn = useSelector((state) => state.login.isLoggedIn);

  // Decode token from localStorage and set in Redux (like other pages do)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && !userProfile) {
      try {
        const user = JSON.parse(atob(token.split('.')[1]));
        dispatch(setLoginSuccess(user));
        // console.log('🔑 Token decoded and set in Redux:', user);
      } catch (e) {
        console.error('❌ Failed to parse token:', e);
      }
    }
  }, [dispatch, userProfile]);

  // Get userId from token - try all possible field names
  const userId = userProfile?._id || userProfile?.sub || userProfile?.userId;
  // console.log('📍 Current userProfile:', userProfile);
  // console.log('📍 Extracted userId:', userId);

  // Fetch schedule
  const {
    data: scheduleData,
    isLoading: scheduleLoading,
    error: scheduleError
  } = useQuery({
    queryKey: ['schedule-today'],
    queryFn: fetchTodaySchedule,
    enabled: isLoggedIn
  });

  // Fetch user predictions (for history)
  const {
    data: predictionsData,
    isLoading: predictionsLoading,
    error: predictionsError,
    refetch: refetchPredictions
  } = useQuery({
    queryKey: ['user-predictions', userId],
    queryFn: () => fetchUserPredictions(userId),
    enabled: isLoggedIn && !!userId
  });

  // Fetch today's prediction
  const {
    data: todayPredictionData,
    isLoading: todayPredictionLoading,
    error: todayPredictionError,
    refetch: refetchTodayPrediction
  } = useQuery({
    queryKey: ['today-prediction', userId],
    queryFn: () => fetchTodayPrediction(userId),
    enabled: isLoggedIn && !!userId
  });

  // Fetch leaderboard
  const {
    data: leaderboardData,
    isLoading: leaderboardLoading,
    error: leaderboardError
  } = useQuery({
    queryKey: ['predictions-leaderboard'],
    queryFn: fetchLeaderboard,
    enabled: isLoggedIn
  });

  // Fetch selected user's history for modal
  const selectedUserIdForQuery = selectedUser?.userId || selectedUser?._id;
  const {
    data: selectedUserHistoryData,
    isLoading: selectedUserHistoryLoading
  } = useQuery({
    queryKey: ['user-history', selectedUserIdForQuery],
    queryFn: () => fetchUserHistory(selectedUserIdForQuery),
    enabled: !!selectedUserIdForQuery
  });

  const selectedUserHistory = selectedUserHistoryData?.predictions || [];

  // Get all matches from schedule
  const allMatches = scheduleData?.matches || [];
  const currentMatch = allMatches?.[0];
  const isMatchLocked = currentMatch?.status !== 'UpComing';
  const otherMatches = (predictionsData?.predictions || []).filter(p => {
    const scheduleMatch = allMatches.find(m => m.matchId === p.matchId);
    return !scheduleMatch || scheduleMatch.status === 'Post' || scheduleMatch.status === 'PostGame';
  });

  // Populate selected predictions from today's predictions on load
  useEffect(() => {
    if (todayPredictionData?.predictions?.length > 0 && allMatches.length > 0) {
      const updates = {};
      todayPredictionData.predictions.forEach(p => {
        updates[p.matchId] = p.predictedWinner;
      });
      setSelectedPredictions(prev => ({ ...prev, ...updates }));
    }
  }, [todayPredictionData, allMatches]);

  const handlePredictionChange = (matchId, winner) => {
    // Toggle logic: if same team is clicked, deselect it
    const currentSelection = selectedPredictions[matchId];
    if (currentSelection === winner) {
      setSelectedPredictions({
        ...selectedPredictions,
        [matchId]: undefined
      });
    } else {
      setSelectedPredictions({
        ...selectedPredictions,
        [matchId]: winner
      });
    }
  };

  // Helper function to check if prediction is correct
  const isPredictionCorrect = (match) => {
    if (match.status !== 'PostGame' || !match.winner) {
      return false;
    }
    const prediction = selectedPredictions[match.matchId];
    return prediction === match.winner;
  };

  const handleSavePrediction = async (match) => {
    console.log('🎯 Attempting to save prediction for match:', match);
    const winner = selectedPredictions[match.matchId];
    
    console.log('🔹 Match ID:', match.matchId);
    console.log('🔹 Winner selection:', winner);
    console.log('🔹 User ID:', userId);
    
    if (!winner) {
      message.warning('Please select a prediction before saving');
      return;
    }

    setSavingMatchId(match.matchId);
    try {
      const response = await savePrediction(userId, match.matchId, winner);
      message.success(response.message);
      refetchTodayPrediction();
      refetchPredictions();
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message || 'Failed to save prediction';
      console.error('❌ Error saving prediction:', errorMsg);
      message.error(errorMsg);
    } finally {
      setSavingMatchId(null);
    }
  };

  const handleClearPrediction = async (match) => {
    console.log('🗑️ Clearing prediction for match:', match);
    setSavingMatchId(match.matchId);
    try {
      const response = await savePrediction(userId, match.matchId, null);
      message.success(response.message);
      setSelectedPredictions({
        ...selectedPredictions,
        [match.matchId]: undefined
      });
      refetchTodayPrediction();
      refetchPredictions();
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message || 'Failed to clear prediction';
      console.error('❌ Error clearing prediction:', errorMsg);
      message.error(errorMsg);
    } finally {
      setSavingMatchId(null);
    }
  };

  // AG Grid Column Definitions
  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
    filter: true,
    flex: 1,
    minWidth: 100
  }), []);

  // Leaderboard columns for AG Grid
  const leaderboardColumnDefs = useMemo(() => [
    {
      field: 'rank',
      headerName: 'Rank',
      headerTooltip: 'Rank',
      width: 80,
      valueGetter: (params) => params.node.rowIndex + 1,
      tooltipValueGetter: (params) => `Rank: ${params.node.rowIndex + 1}`,
      sortable: false,
      filter: false
    },
    {
      field: 'userName',
      headerName: 'User Name',
      headerTooltip: 'User Name',
      width: 150,
      cellRenderer: (params) => (
        <span
          className="leaderboard-username-link"
          onClick={() => setSelectedUser(params.data)}
          title="Click to view history"
        >
          {params.data.userName}
        </span>
      ),
      tooltipValueGetter: (params) => `${params.data.userName} — click to view history`
    },
    {
      field: 'totalPoints',
      headerName: 'Total Points',
      headerTooltip: 'Total Points',
      width: 120,
      tooltipValueGetter: (params) => `Total Points: ${params.data.totalPoints}`,
      sort: 'desc'
    },
    {
      field: 'currentStreak',
      headerName: 'Current Streak',
      headerTooltip: 'Current Streak',
      width: 140,
      tooltipValueGetter: (params) => `Current Streak: ${params.data.currentStreak}`
    },
    {
      field: 'maxStreak',
      headerName: 'Max Streak',
      headerTooltip: 'Max Streak',
      width: 120,
      tooltipValueGetter: (params) => `Max Streak: ${params.data.maxStreak}`
    }
  ], [setSelectedUser]);

  // History columns for AG Grid
  const historyColumnDefs = useMemo(() => [
    {
      field: 'matchId',
      headerName: 'Match',
      headerTooltip: 'Match',
      width: 120,
      valueGetter: (params) => `${params.data.team1} vs ${params.data.team2}`,
      tooltipValueGetter: (params) => `${params.data.team1} vs ${params.data.team2}`,
      sortable: false
    },
    {
      field: 'matchNumber',
      headerName: 'Match #',
      headerTooltip: 'Match Number',
      width: 100,
      tooltipValueGetter: (params) => `Match #${params.data.matchNumber}`
    },
    {
      field: 'predictedWinner',
      headerName: 'Predicted Winner',
      headerTooltip: 'Predicted Winner',
      width: 150,
      tooltipValueGetter: (params) => params.data.predictedWinner
    },
    {
      field: 'isCorrect',
      headerName: 'Correct',
      headerTooltip: 'Correct',
      width: 100,
      cellRenderer: (params) => (
        <span style={{ color: params.value ? '#10b981' : '#dc3545', fontWeight: 'bold' }}>
          {params.value ? '✓' : '✗'}
        </span>
      ),
      tooltipValueGetter: (params) => params.value ? 'Correct ✓' : 'Incorrect ✗',
      sortable: false
    },
    {
      field: 'streakLength',
      headerName: 'Streak Length',
      headerTooltip: 'Streak Length',
      width: 130,
      tooltipValueGetter: (params) => `Streak Length: ${params.data.streakLength}`
    },
    {
      field: 'streakPoints',
      headerName: 'Streak Points',
      headerTooltip: 'Streak Points',
      width: 130,
      tooltipValueGetter: (params) => `Streak Points: ${params.data.streakPoints}`
    },
    {
      field: 'correctPoints',
      headerName: 'Correct Points',
      headerTooltip: 'Correct Points',
      width: 140,
      tooltipValueGetter: (params) => `Correct Points: ${params.data.correctPoints}`
    },
    {
      field: 'totalPoints',
      headerName: 'Total Points',
      headerTooltip: 'Total Points',
      width: 120,
      tooltipValueGetter: (params) => `Total Points: ${params.data.totalPoints}`
    }
  ], []);

  const leaderboardSorted = [...(leaderboardData?.leaderboard || [])].sort(
    (a, b) => b.totalPoints - a.totalPoints
  );

  return (
    <div className="prediction-page">
      {/* Layout Container with percentage-based columns */}
      <div className="prediction-layout-wrapper">
        {/* Left Margin 5% */}
        <div className="layout-margin-left"></div>

        {/* Prediction Card 24% */}
        <div className="layout-card prediction-card-wrapper">
          <div className="match-section">
            <h3 className="section-title">Today's Prediction</h3>
            {scheduleLoading ? (
              <div className="loading-center">
                <Spin size="large" />
              </div>
            ) : scheduleError || allMatches.length === 0 ? (
              <div className="empty-state">
                <Empty description="No upcoming matches today" />
              </div>
            ) : (
              <div className="matches-container">
                {allMatches.map((match) => (
                  <Card key={match.matchId} className="match-card">
                    {/* Match Header with Title and Status */}
                    <div className="match-header">
                      <div className="match-title-section">
                        <h4 className="match-title">Match {match.matchNumber}</h4>
                        {isPredictionCorrect(match) && (
                          <Chip 
                            label="Correct" 
                            size="small"
                            sx={{ 
                              background: 'rgba(16, 185, 129, 0.8)',
                              color: '#ffffff',
                              fontSize: '10px',
                              fontWeight: '600',
                              height: '20px',
                              marginLeft: '8px'
                            }} 
                          />
                        )}
                      </div>
                      <span className={`status-badge status-${match.status}`}>
                        {match.status}
                      </span>
                    </div>

                    {/* Match Details as Chips (1 Row) */}
                    <div className="match-chips">
                      <Chip 
                        label={match.venue} 
                        size="small"
                        sx={{ 
                          background: 'rgba(255, 255, 255, 0.1)',
                          color: 'rgba(255, 255, 255, 0.9)',
                          fontSize: '11px'
                        }} 
                      />
                      <Chip 
                        label={match.scheduledAt} 
                        size="small"
                        sx={{ 
                          background: 'rgba(255, 255, 255, 0.1)',
                          color: 'rgba(255, 255, 255, 0.9)',
                          fontSize: '11px'
                        }} 
                      />
                    </div>

                    {/* Prediction Form */}
                    <div className="prediction-form">
                      <p className="form-label">Who will win?</p>

                      {/* Teams Display */}
                      <div className="teams-display">
                        <button
                          className={`team-button ${selectedPredictions[match.matchId] === match.team1 ? 'selected' : ''}`}
                          onClick={() => match.status === 'UpComing' && handlePredictionChange(match.matchId, match.team1)}
                          disabled={match.status !== 'UpComing'}
                        >
                          {match.team1}
                        </button>
                        <span className="vs-divider">VS</span>
                        <button
                          className={`team-button ${selectedPredictions[match.matchId] === match.team2 ? 'selected' : ''}`}
                          onClick={() => match.status === 'UpComing' && handlePredictionChange(match.matchId, match.team2)}
                          disabled={match.status !== 'UpComing'}
                        >
                          {match.team2}
                        </button>
                      </div>

                      {match.status !== 'UpComing' && (
                        <div className="lock-warning">
                          <strong>Match locked</strong> - Predictions cannot be changed
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="action-buttons">
                        <Button
                          type="primary"
                          disabled={!selectedPredictions[match.matchId] || match.status !== 'UpComing' || savingMatchId === match.matchId}
                          loading={savingMatchId === match.matchId}
                          onClick={() => handleSavePrediction(match)}
                          className="btn-save"
                        >
                          Save
                        </Button>
                        {selectedPredictions[match.matchId] && match.status === 'UpComing' && (
                          <Button
                            danger
                            disabled={savingMatchId === match.matchId}
                            loading={savingMatchId === match.matchId}
                            onClick={() => handleClearPrediction(match)}
                            className="btn-clear"
                          >
                            Clear
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Gap between Prediction and Leaderboard */}
        <div className="layout-gap-small"></div>

        {/* Leaderboard Card */}
        <div className="layout-card leaderboard-card-wrapper">
          <div className="leaderboard-section">
            <h3 className="section-title">Leaderboard</h3>
            {leaderboardLoading ? (
              <div className="loading-center">
                <Spin size="large" />
              </div>
            ) : leaderboardError ? (
              <div className="empty-state">
                <Empty description="Failed to load leaderboard" />
              </div>
            ) : leaderboardSorted.length === 0 ? (
              <div className="empty-state">
                <Empty description="No users yet" />
              </div>
            ) : (
              <div className="ag-theme-alpine-dark leaderboard-grid-container">
                <AgGridReact
                  rowData={leaderboardSorted}
                  columnDefs={leaderboardColumnDefs}
                  defaultColDef={defaultColDef}
                  animateRows={true}
                  domLayout="normal"
                  suppressHorizontalScroll={false}
                />
              </div>
            )}
          </div>
        </div>

        {/* Right Margin 5% */}
        <div className="layout-margin-right"></div>
      </div>

      {/* User History Modal */}
      <Modal
        open={!!selectedUser}
        onCancel={() => setSelectedUser(null)}
        footer={null}
        title={
          <span className="history-modal-title">
            {selectedUser?.userName}'s History
          </span>
        }
        width={720}
        className="history-modal"
        centered
      >
        {selectedUserHistoryLoading ? (
          <div className="loading-center" style={{ padding: '40px' }}>
            <Spin size="large" />
          </div>
        ) : selectedUserHistory.length === 0 ? (
          <Empty description="No history yet" style={{ padding: '40px' }} />
        ) : (
          <div className="ag-theme-alpine-dark history-modal-grid">
            <AgGridReact
              rowData={selectedUserHistory}
              columnDefs={historyColumnDefs}
              defaultColDef={defaultColDef}
              animateRows={true}
              domLayout="normal"
              suppressHorizontalScroll={false}
            />
          </div>
        )}
      </Modal>

    </div>
  );
}
